import {Date as ApiDate} from 'gabi-api-ts/v2/common/date/date';
import {SignalEventConfiguration, SignalType} from 'gabi-api-ts/v2/common/signals/signals';
import {PatientType} from 'gabi-api-ts/v2/patient/common/patient_common';
import {HasData} from 'gabi-api-ts/v2/signal/query/signal_query';
import {SignalQueryServiceClient} from 'gabi-api-ts/v2/signal/query/signal_query.client';
import {useTranslation} from 'react-i18next';
import {Area, Bar, ComposedChart, Line, ReferenceLine, Tooltip, XAxis, YAxis} from 'recharts';
import {CategoricalChartFunc} from 'recharts/types/chart/generateCategoricalChart';
import styled from 'styled-components';

import {AverageValue} from '@/components/business/analytics/health-report/health-report-average-value';
import {HealthReportStatisticsTrend} from '@/components/business/analytics/health-report/health-report-statistics-trend';
import {ChartAxisTickColored} from '@/components/business/analytics/patient/patient-chart-axis/chart-axis-tick-colored';
import {ChartAxisTickDate} from '@/components/business/analytics/patient/patient-chart-axis/chart-axis-tick-date';
import {ChartAxisTickDuration} from '@/components/business/analytics/patient/patient-chart-axis/chart-axis-tick-duration';
import {getDomainForDuration, getDomainForSignalType} from '@/components/business/analytics/timeline/timeline-chart-domains';
import {ChartContainer} from '@/components/layout/chart-container';
import LoadingView from '@/components/static/loading-view';
import ComponentErrorMessage from '@/components/widgets/component-error-message';
import {usePatientData} from '@/hooks/backend/use-patient-data';
import {useBackendQuery} from '@/hooks/use-backend-query';
import {defaultEventConfiguration} from '@/model/defaults/default-event-configuration';
import {SignalDefinitions} from '@/model/signal-definitions/signal-definition';
import {transformAverageSummary} from '@/model/transformers/transform-average-summary';
import {ignoreVP39} from '@/services/grpc/grpc-error-handlers';
import {colorPalette} from '@/themes/darkmode';
import {getMax} from '@/util/array-util';
import {formatDateUniversal, formatSecondsToHms} from '@/util/date-utils';
import {getSignalEventConfig, getSignalTypeLabel, getSignalTypeUnit} from '@/util/signal-type-util';
import {roundDurationHoursToQuarterPrecision} from '@/util/time-utils';

type HealthReportStatisticsPerDayProps = {
    patientId: string,
    signalType: SignalType,
    lastRecordingDate: ApiDate,
    dateFrom: ApiDate;
    dateTo: ApiDate;
    ignoreUnifiedDuration?: boolean,
    displayPreviousNightsAverage: boolean,
    onClick: (date: Date) => void,
}

type StatisticPerDayFormatted = {
    date: string;
    average: number | null;
    hasEnoughData: boolean;
    reliableDurationInSeconds: number;
    duration: number | null;
    standardDeviation: [number, number] | null;
}

type HealthReportStatisticsPerDayTooltipProps = {
    payload?: Array<{
        payload?: StatisticPerDayFormatted;
    }>;
}

/*
  TODO this component would benefit a split in 3 :
  - the charting area
  - the statistics per day as displayed in the health report, ie this component with displayPreviousNightsAverage=false
  - the statistics per day as displayed in the patients list, ie this component with displayPreviousNightsAverage=true
  This would greatly improve the understability of this component, and avoid using the disabled flag of useBackendQuery
*/

const HealthReportStatisticsPerDay = ({patientId, signalType, lastRecordingDate, dateFrom, dateTo, ignoreUnifiedDuration, displayPreviousNightsAverage, onClick}: HealthReportStatisticsPerDayProps) => {
    const { t } = useTranslation();
    const signalTypeUnit = getSignalTypeUnit(signalType);

    const [patientDataLoading, patientData] = usePatientData({
        patientId: patientId,
        memoize: true,
    });

    const [unifiedDurationLoading, unifiedDuration] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getSignalsUnifiedDuration,
        memoize: true,
        data: {
            patientId: { id: patientId },
            signals: [SignalType.Signal_PR, SignalType.Signal_SPO2],
            from: dateFrom,
            to: dateTo,
        },
    });

    const [statisticsPerDayLoading, statisticsPerDay] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getStatisticsPerDay,
        memoize: true,
        data: {
            patientId: { id: patientId },
            signalType: signalType,
            from: dateFrom,
            to: dateTo,
        },
    });

    const [signalsEventConfigurationLoading, signalsEventConfiguration] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getEventConfiguration,
        memoize: true,
        data: { patientId: { id: patientId } },
        grpcOptions: {
            errorHandler: ignoreVP39(defaultEventConfiguration),
        },
    });

    const [signalAverageSummaryLoading, signalAverageSummary] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getSignalAverageSummary,
        memoize: true,
        disabled: !displayPreviousNightsAverage,
        data: {
            patientId: { id: patientId },
            signalType: signalType,
            date: lastRecordingDate,
            nbPreviousDays: 14,
        },
        transformer: transformAverageSummary,
    });

    const [signalAverageForPeriodLoading, signalAverageForPeriod] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getSignalAverageForPeriod,
        memoize: true,
        data: {
            patientId: { id: patientId },
            signalType: signalType,
            from: dateFrom,
            to: dateTo,
        },
    });

    const loading = (
        (patientDataLoading) ||
        (unifiedDurationLoading || !unifiedDuration) ||
        (statisticsPerDayLoading || !statisticsPerDay) ||
        (signalsEventConfigurationLoading || !signalsEventConfiguration) ||
        (signalAverageSummaryLoading) ||
        (signalAverageForPeriodLoading)
    );

    const signalEventConfiguration: SignalEventConfiguration = signalsEventConfigurationLoading ? {
        low: { value: -1000 },
        high: { value: -1000 },
        sensibility: { seconds: 3600 },
    } : getSignalEventConfig(signalsEventConfiguration!.configuration!, signalType);

    const statisticsPerDayFormatted: StatisticPerDayFormatted[] = [];
    if (!statisticsPerDayLoading && statisticsPerDay) {
        statisticsPerDay.statisticsPerDay.forEach(statistic => {
            // TODO temporary - accept lower hasdata for parent
            const hasEnoughData = patientData!.patientType === PatientType.Adult ? (
                statistic.hasRecording && statistic.hasData >= HasData.HAS_RELIABLE_DATA
            ) : (
                statistic.hasRecording && statistic.hasData >= HasData.HAS_MIN_RELIABLE_DATA
            );
            statisticsPerDayFormatted.push({
                date: statistic.date,
                hasEnoughData: hasEnoughData,
                average: (hasEnoughData ? statistic.average : null),
                reliableDurationInSeconds: statistic.reliableDurationInSeconds,
                duration: (statistic.hasRecording && statistic.reliableDurationInSeconds > 0) ? roundDurationHoursToQuarterPrecision(statistic.reliableDurationInSeconds) : null,
                standardDeviation: ((hasEnoughData && statistic.reliableDurationInSeconds > 0) ? [
                    statistic.min,
                    statistic.max
                ] : null)
            });
        });
    }

    const signalRange = (statisticsPerDayLoading || !statisticsPerDay) ? [0, 1000] : [statisticsPerDay.rangeMin, statisticsPerDay.rangeMax];

    const handleClick: CategoricalChartFunc = (rechartData) => {
        if (onClick) {
            if (rechartData && rechartData.activePayload && rechartData.activePayload[0]) {
                const rechartsPayload = rechartData?.activePayload[0].payload;
                if (rechartsPayload) {
                    const selectedDate = new Date(rechartsPayload.date);
                    const duration = rechartsPayload.reliableDurationInSeconds;
                    if (duration && duration > 0) {
                        onClick(selectedDate);
                    }
                }
            }
        }
    };

    const renderTooltip = (data: HealthReportStatisticsPerDayTooltipProps) => {
        if (data?.payload && data?.payload[0]?.payload) {
            const payload = data.payload[0].payload;
            const average = payload.average;
            const reliableDurationInSeconds = payload.reliableDurationInSeconds ?? 0;
            const hasEnoughData = payload.hasEnoughData;

            return (
                <div className="chart-tooltip">
                    <strong>{formatDateUniversal(new Date(payload.date + ' 00:00:00'))}</strong><br />
                    {reliableDurationInSeconds ? (
                        <span className="tooltip-duration">{t('healthReport.statistics.last7nights.duration')}: {formatSecondsToHms(reliableDurationInSeconds)}</span>
                    ) : (
                        <span>{t('healthReport.statistics.last7nights.noRecordingForThisDay')}</span>
                    )}
                    {!reliableDurationInSeconds ? (
                        <span>{t('healthReport.statistics.last7nights.noRecordingForThisDay')}</span>
                    ) : (!hasEnoughData ? (
                        <span>{t('healthReport.statistics.last7nights.notEnoughDataForThisDay')}</span>
                    ) : (
                        <span className="tooltip-average">{t('healthReport.statistics.last7nights.average')}: {Math.round(average!)}{signalTypeUnit}</span>
                    ))}
                </div>
            );
        }
        return null;
    };

    const domainForSignalType = getDomainForSignalType(signalType, signalRange[0], signalRange[1]);

    if (patientId && dateFrom && dateTo && signalEventConfiguration) {
        const title = getSignalTypeLabel(signalType);

        const topRightContent = displayPreviousNightsAverage ? (<>
            {signalAverageSummary && (
                <div className="average-block">
                    <p>
                        <HealthReportStatisticsTrend
                            trend={signalAverageSummary.trend}
                        />
                        {t('healthReport.aggregatedSignal.avgRecording')}{' '}
                        <AverageValue averageObj={signalAverageSummary.averageValue ?? null} unit={signalTypeUnit} precision={0} />
                    </p>
                    <p>
                        {t('healthReport.aggregatedSignal.avgLast14Nights')}{' '}
                        <AverageValue averageObj={signalAverageSummary.averagePrevious ?? null} unit={signalTypeUnit} precision={0} />
                    </p>
                </div>
            )}
        </>) : (
            <div className="average-block">
                <p>
                    {t('healthReport.statistics.averageForPeriod')}{' '}
                    <AverageValue averageObj={signalAverageForPeriod ?? null} unit={signalTypeUnit} precision={0} />
                </p>
            </div>
        );

        const tooltipText = <>
            <p>
                {signalType === SignalType.Signal_PR &&
                    <>{t('infoButton.average.pulseRate')}&nbsp;</>
                }
                {signalType === SignalType.Signal_SPO2 &&
                    <>{t('infoButton.average.spo2')}&nbsp;</>
                }
                {t('infoButton.average.reliableData')}
            </p>
            <p>
                {t('infoButton.average.duration')}
            </p>
        </>;

        const signalColors = (SignalDefinitions[signalType] ?? SignalDefinitions[-1]).colors;

        if (!loading) {
            const maxDuration = ignoreUnifiedDuration ? getMax(statisticsPerDayFormatted, (point) => point.reliableDurationInSeconds).reliableDurationInSeconds : unifiedDuration?.maxUnifiedDurationInSeconds;
            const durationDomain = getDomainForDuration(maxDuration!);

            return (
                <StyledWrapper className="health-report-statistics-per-day" color={signalColors.light}>
                    <ChartContainer
                        title={title}
                        topRightContent={topRightContent}
                        infoTooltipTitle={`${title} - ${t('infoButton.average.title')}`}
                        infoTooltipText={tooltipText}
                        infoTooltipOverlayPosition="bottomLeft"
                    >
                        {(statisticsPerDayFormatted.length <= 0) ? (
                            <div className="empty-message">{t('healthReport.statistics.last7nights.empty')}</div>
                        ) : (
                            <ComposedChart data={statisticsPerDayFormatted} margin={{ top: 0, right: 0, bottom: 0, left: 10 }} onClick={handleClick}>
                                <XAxis
                                    dataKey="date"
                                    interval="equidistantPreserveStart"
                                    tick={<ChartAxisTickDate textAnchor="middle" />}
                                />
                                <YAxis
                                    yAxisId="left"
                                    orientation="left"
                                    dataKey="average"
                                    tick={(props) => <ChartAxisTickColored {...props} unit={signalTypeUnit} fill={signalColors.light} />}
                                    tickCount={4}
                                    domain={domainForSignalType.domain}
                                    ticks={domainForSignalType.ticks}
                                    interval={0}
                                    padding={{ top: 10 }}
                                    width={35}
                                />
                                <YAxis
                                    yAxisId="right"
                                    dataKey="duration"
                                    orientation="right"
                                    tick={<ChartAxisTickDuration textAnchor="start"/>}
                                    padding={{ top: 10 }}
                                    width={37}
                                    domain={durationDomain.domain}
                                    ticks={durationDomain.ticks}
                                    interval={0}
                                />

                                <Bar
                                    dataKey="duration"
                                    fill={colorPalette.thirdBackground}
                                    yAxisId="right"
                                    barSize={40}
                                />

                                {signalType !== SignalType.Signal_SPO2 &&
                                    <ReferenceLine
                                        y={signalEventConfiguration.high!.value}
                                        label={{
                                            position: (signalEventConfiguration.high!.value >= (domainForSignalType.domain[1] - (5))) ? 'bottom' : 'top',
                                            value: `${signalEventConfiguration.high!.value}${signalTypeUnit}`,
                                            fill: signalColors.clear
                                        }}
                                        stroke={signalColors.clear}
                                        strokeDasharray="4"
                                        yAxisId="left"
                                    />
                                }

                                <ReferenceLine
                                    y={signalEventConfiguration.low!.value}
                                    label={{
                                        position: (signalEventConfiguration.low!.value <= (domainForSignalType.domain[0] + (5))) ? 'top' : 'bottom',
                                        value: `${signalEventConfiguration.low!.value}${signalTypeUnit}`,
                                        fill: signalColors.clear,
                                    }}
                                    stroke={signalColors.clear}
                                    strokeDasharray="4"
                                    yAxisId="left"
                                />

                                <Area
                                    type="monotone"
                                    dot={false}
                                    activeDot={false}
                                    dataKey="standardDeviation"
                                    fill={signalColors.normal}
                                    fillOpacity="0.3"
                                    strokeWidth={0}
                                    yAxisId="left"
                                    connectNulls
                                />

                                <Line
                                    type="monotone"
                                    dot={{ fill: signalColors.normal }}
                                    dataKey="average"
                                    stroke={signalColors.normal}
                                    strokeWidth={2}
                                    isAnimationActive={false}
                                    yAxisId="left"
                                />

                                <Tooltip
                                    content={renderTooltip}
                                    isAnimationActive={false}
                                />
                            </ComposedChart>
                        )}
                    </ChartContainer>
                </StyledWrapper>
            );
        }
        else {
            return (
                <StyledWrapper className="health-report-statistics-per-day" color={signalColors.light}>
                    <ChartContainer title={title}>
                        <LoadingView color={signalColors.normal} />
                    </ChartContainer>
                </StyledWrapper>
            );
        }
    }
    else {
        return <ComponentErrorMessage component="HealthReportStatisticsPerDay" />;
    }
};

//language=SCSS
const StyledWrapper = styled.div`
& {
    min-height: 260px;
    height: 100%;
    display: flex;
    flex-direction: column;
    
    > * {
        flex-grow: 1;
    }

    svg {
        cursor: pointer;
    }
    
    .tooltip-average {
        color: ${props => props.color};
    }
    
    .tooltip-duration {
        color: ${colorPalette.clearColor};
    }
    
    .average-block {
        p span {
            color: ${props => props.color};
        }
        img.warningFlag {
            width: 15px;
            margin-right: 7px;
            margin-top: -3px;
        }
    }
}
`;

export {HealthReportStatisticsPerDay};
